function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, forwardRef } from 'react';
import { spacing, themeLight } from '@naf/theme';
import { Delete } from '@styled-icons/material-outlined/Delete';
import { Topic } from '@styled-icons/material-outlined/Topic';
import styled, { css } from 'styled-components';
import { Text, TextVariant } from '@naf/text';
import { Loader } from '@naf/loader';
import { createNorwegianDateStringWithTime } from './assets/createStringDate';
import { propValidationConfig } from '../../propValidationConfig';
export const imageTypeRegex = /(image)\/(png|jpg|jpeg)/gm;
export const DocumentList = /*#__PURE__*/forwardRef(({
  files,
  setFiles,
  ...rest
}, ref) => {
  const [images, setImages] = useState([]);
  const [loadingStates, setLoadingStates] = useState({});
  const [hoverOnLoader, setHoverOnLoader] = useState(false);
  const fileIds = files.map(file => file.name);
  const handleMouseOver = () => {
    setHoverOnLoader(true);
  };
  const handleMouseLeave = () => {
    setHoverOnLoader(false);
  };

  // Truncate excessive long file names
  const truncateFileName = name => {
    const lastIndex = name.lastIndexOf('.');
    const fileName = name.slice(0, lastIndex);
    if (fileName.length >= 53) {
      const extension = name.slice(lastIndex + 1);
      const truncatedFileName = fileName.slice(0, 50);
      const stringEnd = fileName.slice(-3);
      return `${truncatedFileName}...${stringEnd}.${extension}`;
    }
    return name;
  };
  const createFileSize = fileSize => {
    if (fileSize < 2 ** 10) {
      return `${fileSize.toFixed(2)} B`;
    }
    if (fileSize < 2 ** 20) {
      return `${(fileSize / 2 ** 10).toFixed(2)} KB`;
    }
    if (fileSize < 2 ** 30) {
      return `${(fileSize / 2 ** 20).toFixed(2)} MB`;
    }
    return `${(fileSize / 2 ** 30).toFixed(2)} GB`;
  };
  const lastModifiedAt = lastModified => {
    const date = new Date(lastModified);
    return createNorwegianDateStringWithTime(date);
  };
  const isImageFile = file => {
    return !!file.type.match(imageTypeRegex);
  };

  // Create image previews
  useEffect(() => {
    const readers = [];
    let isCancel = false;
    if (files.length) {
      const promises = files.map(file => new Promise((resolve, reject) => {
        const fileReader = new FileReader();
        readers.push(fileReader);
        fileReader.onload = e => {
          if (e.target) {
            const {
              result
            } = e.target;
            if (result) {
              resolve(result);
            }
          }
        };
        fileReader.onabort = () => {
          reject(new Error('File reading aborted'));
        };
        fileReader.onerror = () => {
          reject(new Error('Failed to read file'));
        };
        fileReader.readAsDataURL(file);
      }));
      Promise.all(promises).then(tempImages => {
        if (!isCancel) {
          setImages(tempImages);
        }
      }).catch(reason => {
        // eslint-disable-next-line no-console
        console.log(reason);
      });
    }
    return () => {
      isCancel = true;
      readers.forEach(fileReader => {
        if (fileReader.readyState === 1) {
          fileReader.abort();
        }
      });
    };
  }, [files]);

  // Create loading state for uploads
  useEffect(() => {
    let loaders = {
      ...loadingStates
    };
    const readers = [];
    let isCancel = false;
    if (files.length) {
      files.forEach(file => {
        const reader = new FileReader();
        readers.push(reader);
        const {
          name
        } = file;
        reader.onloadstart = () => {
          Object.assign(loaders, {
            [name]: reader.readyState
          });
          if (!isCancel) {
            setLoadingStates({
              ...loaders
            });
          }
        };
        reader.onload = () => {
          Object.assign(loaders, {
            [name]: reader.readyState
          });
          if (!isCancel) {
            setLoadingStates({
              ...loaders
            });
          }
        };
        reader.readAsText(file);
      });
    } else {
      loaders = {};
      setLoadingStates(loaders);
    }
    return () => {
      isCancel = true;
      readers.forEach(reader => {
        if (reader.readyState === 1) {
          reader.abort();
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);
  return /*#__PURE__*/React.createElement(Container, _extends({}, rest, {
    ref: ref
  }), /*#__PURE__*/React.createElement(Ul, {
    "aria-label": "liste over opplastede filer"
  }, /*#__PURE__*/React.createElement(Text, {
    variant: TextVariant.ArticleTextHeader
  }, files.length > 0 ? `Opplastede filer` : `Ingen opplastede filer`), files.map((file, index) => /*#__PURE__*/React.createElement(Li, {
    key: file.name,
    id: fileIds[index],
    hoverOnLoader: hoverOnLoader
  }, /*#__PURE__*/React.createElement(File, null, isImageFile(file) && (images[index] ? /*#__PURE__*/React.createElement(Img, {
    src: images[index],
    alt: "preview",
    width: "32",
    height: "32"
  }) : /*#__PURE__*/React.createElement(Topic, {
    size: 32
  })), !isImageFile(file) && /*#__PURE__*/React.createElement(Topic, {
    size: 32
  }), /*#__PURE__*/React.createElement(FileDescription, null, /*#__PURE__*/React.createElement(Text, {
    variant: TextVariant.BodyTextSubHeader
  }, truncateFileName(file.name)), /*#__PURE__*/React.createElement(SmallText, {
    variant: TextVariant.Small
  }, `${lastModifiedAt(file.lastModified)} • ~${createFileSize(file.size)}`))), loadingStates[file.name] === 2 && /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: () => setFiles(files.filter(fileInArray => file.name !== fileInArray.name)),
    "aria-label": "Slett fil"
  }, /*#__PURE__*/React.createElement(Delete, {
    size: 24
  })), loadingStates[file.name] === 1 && /*#__PURE__*/React.createElement("div", {
    onMouseOver: handleMouseOver,
    onFocus: handleMouseOver,
    onMouseLeave: handleMouseLeave,
    onBlur: handleMouseLeave
  }, hoverOnLoader ? /*#__PURE__*/React.createElement(Button, {
    type: "button",
    onClick: () => setFiles(files.filter(fileInArray => file.name !== fileInArray.name)),
    "aria-label": "Slett fil"
  }, /*#__PURE__*/React.createElement(Delete, {
    size: 24
  })) : /*#__PURE__*/React.createElement(Loader, null))))));
});
const Container = styled.div.withConfig({
  displayName: "documentList__Container",
  componentId: "sc-557ea0-0"
})(["display:flex;flex-direction:column;margin-bottom:", ";"], spacing.space24);
const Ul = styled.ul.withConfig({
  displayName: "documentList__Ul",
  componentId: "sc-557ea0-1"
})(["list-style-type:none;padding:0;margin:0;h4{margin:0;}"]);
const Li = styled.li.withConfig(propValidationConfig).withConfig({
  displayName: "documentList__Li",
  componentId: "sc-557ea0-2"
})(["border-bottom:1px solid rgba(0,0,0,0.12);padding-bottom:", ";display:flex;justify-content:space-between;button:hover,button:focus,button:focus-visible{path{color:", ";}}&:not(:first-of-type){margin-top:", ";}&:last-child{margin-bottom:", ";}", ""], spacing.space20, ({
  theme
}) => theme.typography ? theme.typography.defaultText : themeLight.typography.defaultText, spacing.space24, spacing.space24, ({
  hoverOnLoader
}) => !hoverOnLoader && css(["div:last-child{display:flex;justify-content:center;align-items:center;}div:last-child > div{width:", ";height:", ";margin:auto ", ";}"], spacing.space24, spacing.space24, spacing.space12));
const Button = styled.button.withConfig({
  displayName: "documentList__Button",
  componentId: "sc-557ea0-3"
})(["border:none;margin:0;width:auto;overflow:visible;background:transparent;font:inherit;line-height:normal;touch-action:manipulation;appearance:none !important;cursor:pointer;color:", ";padding:0 ", ";"], ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText, spacing.space12);
const FileDescription = styled.span.withConfig({
  displayName: "documentList__FileDescription",
  componentId: "sc-557ea0-4"
})(["display:flex;flex-direction:column;&:not(:first-of-type){margin-left:", ";}p:not(:first-of-type){margin-top:", ";}"], spacing.space16, spacing.space4);
const File = styled.div.withConfig({
  displayName: "documentList__File",
  componentId: "sc-557ea0-5"
})(["display:flex;align-items:center;margin-right:", ";"], spacing.space48);
const Img = styled.img.withConfig({
  displayName: "documentList__Img",
  componentId: "sc-557ea0-6"
})(["object-fit:contain;"]);
const SmallText = styled(Text).withConfig({
  displayName: "documentList__SmallText",
  componentId: "sc-557ea0-7"
})(["color:", ";"], ({
  theme
}) => theme.typography ? theme.typography.subtleText : themeLight.typography.subtleText);
//# sourceMappingURL=documentList.js.map